import { EmployeeCountOptions } from '../../constants/statsOptions'

const EmployeeCountIds = EmployeeCountOptions.map(opt => opt.id)

/**
 * Parse min/max range from a display value string
 * Handles formats like "1 - 10", "1,001 - 5,000", "More than 1,000,000"
 */
function parseRangeFromValue (value: string): { min: number, max: number } {
  if (value.toLowerCase().startsWith('more than')) {
    const num: number = parseInt(value.replace(/[^0-9]/g, ''), 10)
    return { min: num + 1, max: Infinity }
  }

  const numbers: string[] | null = value.match(/[\d,]+/g)
  if (!numbers || numbers.length < 2) {
    return { min: 0, max: Infinity }
  }

  return {
    min: parseInt(numbers[0].replace(/,/g, ''), 10),
    max: parseInt(numbers[1].replace(/,/g, ''), 10)
  }
}

/**
 * Get employee count options with dynamically parsed ranges
 */
function getEmployeeCountRanges (): Array<{ id: string, value: string, min: number, max: number }> {
  return EmployeeCountOptions.map(opt => ({
    ...opt,
    ...parseRangeFromValue(opt.value)
  }))
}

/**
 * Extract all numbers from a string
 */
function extractNumbers (value: string): number[] {
  const matches: string[] | null = value.match(/[\d,]+/g)
  if (!matches) {
    return []
  }
  return matches
    .map(m => parseInt(m.replace(/,/g, ''), 10))
    .filter(n => !isNaN(n) && n > 0)
}

/**
 * Find the matching range for a given number
 */
function findRangeForNumber (num: number): string | undefined {
  const ranges = getEmployeeCountRanges()
  const matchingRange = ranges.find(range => num >= range.min && num <= range.max)
  return matchingRange?.id
}

/**
 * Normalize an employee count input to the appropriate range ID
 * Handles various formats:
 * - Numbers: 500, 1000
 * - Strings with numbers: "500", "1000+", "5,000", "~1000"
 * - Already valid IDs: "1kTo5k", "500To1k"
 * - Ranges: "10,000 - 25,000", "1,000,000 - 750,000", "500 to 1000"
 *   (uses the highest number to determine the band)
 *
 * @param input - Raw employee count value
 * @returns The matching range ID or undefined if unparseable
 */
export default function normalizeEmployeeCount (input: string | number | undefined): string | undefined {
  if (input === undefined || input === null || input === '') {
    return undefined
  }

  if (typeof input === 'number') {
    if (input < 1) {
      return undefined
    }
    return findRangeForNumber(input)
  }

  const stringValue: string = String(input).trim()
  if (EmployeeCountIds.includes(stringValue)) {
    return stringValue
  }

  const numbers: number[] = extractNumbers(stringValue)
  if (numbers.length === 0) {
    return undefined
  }

  const maxNumber: number = Math.max(...numbers)

  return findRangeForNumber(maxNumber)
}
